import React from "react";

import { getPendingArtworkOptimizationJobs } from "../../store/artworks/artworks.api";
import { PendingArtworkOptimizationJobsResponse } from "../../types/artworkOptimization";

const getApiErrorMessage = (caught: any) =>
  caught?.response?.data?.error?.message ||
  caught?.response?.data?.msg ||
  "Pending optimizations could not be loaded.";

export const usePendingArtworkOptimizations = (enabled: boolean, active: boolean) => {
  const [data, setData] = React.useState<PendingArtworkOptimizationJobsResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const mountedRef = React.useRef(true);
  const requestVersionRef = React.useRef(0);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = React.useCallback(async () => {
    if (!enabled) return;
    const requestVersion = ++requestVersionRef.current;
    setLoading(true);
    try {
      const nextData = await getPendingArtworkOptimizationJobs();
      if (!mountedRef.current || requestVersion !== requestVersionRef.current) return;
      setData(nextData);
      setError(null);
    } catch (caught: any) {
      if (!mountedRef.current || requestVersion !== requestVersionRef.current) return;
      setError(getApiErrorMessage(caught));
    } finally {
      if (mountedRef.current && requestVersion === requestVersionRef.current) setLoading(false);
    }
  }, [enabled]);

  React.useEffect(() => {
    if (!enabled) {
      requestVersionRef.current += 1;
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  const hasRunningJobs = Boolean(
    data?.jobs.some((job) => job.status === "queued" || job.status === "processing")
  );

  React.useEffect(() => {
    if (!enabled || !active || !hasRunningJobs) return;

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const intervalId = window.setInterval(refreshWhenVisible, 5000);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [active, enabled, hasRunningJobs, refresh]);

  return { data, loading, error, refresh };
};
